<template>
  <OperationFormLayoutSkeleton v-if="loading" />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="equipmentId"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="equipment"
    @cancel="$router.back()"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top']"
      :sectionDescription="$t('equipment.details_description')"
      :sectionTitle="$t('equipment.details_title')"
    >
      <FormRow
        :errors="nameErrors"
        :label="$t('operations.operation_name_label_equipment')"
        labelFor="operation_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': nameErrors }">
            <input
              id="operation_name"
              v-model="equipment.name"
            >
          </div>
        </div>
      </FormRow>

      <FormRow
        :label="$t('equipment.equipment_division_label')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <DivisionSelect
            v-model="equipment.division"
            :divisions="divisions"
          />
        </div>
      </FormRow>

      <FormRow
        v-if="equipment.division && equipment.division.isNew"
        :errors="divisionNameErrors"
        :label="$t('equipment.equipment_new_division_label')"
        labelFor="new_division_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': divisionNameErrors }">
            <input
              id="new_division_name"
              v-model="equipment.division.name"
            >
          </div>
        </div>
      </FormRow>

      <FormRow
        :label="$t('equipment.mine')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <Select
            v-model="equipment.mine_id"
            :items="mines"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="displayMine"
          />
        </div>
      </FormRow>

      <FormRow
        :label="$t('equipment.type')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <Select
            v-model="equipment.equipment_type_id"
            :items="types"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="displayType"
          />
        </div>
      </FormRow>

      <FormRow
        :label="$t('equipment.process')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <Select
            v-model="equipment.equipment_process_id"
            :items="processes"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="displayProcess"
          />
        </div>
      </FormRow>
    </OperationFormSection>

    <!-- Diagram section -->
    <NoticeMessageContainer v-if="equipment?.diagram_thumbnail_url && warningText">
      <NoticeMessage
        colorStyle="warning"
        iconStyle="warning"
        :message="warningText"
        :modifiers="['narrow']"
      />
    </NoticeMessageContainer>
    <OperationFormSection
      :modifiers="['border-radius-bottom']"
      :sectionDescription="$t('equipment.diagram_help_text')"
      :sectionTitle="$t('equipment.equipment_diagram')"
    >
      <FormRow :modifiers="['no-padding']">
        <template v-if="diagramImage">
          <img
            class="diagram-image"
            :src="diagramImage"
          >
          <FormStandAloneErrorMessage
            v-if="!imageValid"
            :errorMessage="imageValidationMessage"
          />
          <div
            :class="{
              'form-layout__row--mobile form-layout__row--inline': equipmentId,
              'form-layout__row--margin-top': true,
            }"
          >
            <div :class="{ 'form-layout__column form-layout__column--inline-button': equipmentId }">
              <label
                class="diagram-button button"
                for="hme_change_diagram"
              >
                {{ $t('equipment.change_diagram') }}
                <FileUploader
                  id="hme_change_diagram"
                  :acceptedFileTypes="acceptedFileTypes"
                  fileType="image"
                  :label="$t('issues.add_image')"
                  :modifiers="['hide']"
                  :multiple="false"
                  @update-blob="updateBlob"
                  @update-reader="updateReader"
                />
              </label>
            </div>
            <div
              v-if="equipmentId"
              class="form-layout__column form-layout__column--inline-button"
            >
              <router-link
                class="button"
                :to="{ name: 'equipmentManageRegions' }"
              >
                <ManageRegionSvg class="button__icon button--margin-right" />
                {{ $t('equipment.manage_regions') }}
              </router-link>
            </div>
          </div>
        </template>
        <label
          v-else
          class="diagram-button button"
          for="diagram"
        >
          {{ $t('equipment.upload_diagram') }}
          <FileUploader
            id="diagram"
            :acceptedFileTypes="acceptedFileTypes"
            fileType="image"
            :label="$t('issues.add_image')"
            :modifiers="['hide']"
            :multiple="false"
            @update-blob="updateBlob"
            @update-error="updateError"
            @update-reader="updateReader"
          />
        </label>
        <FormStandAloneErrorMessage
          v-if="!imageValid && !diagramImage"
          :errorMessage="imageValidationMessage"
        />
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import I18n from 'i18n-js';
import { computed, inject, onMounted, reactive, ref } from 'vue';

// Utils
import { acceptedFileTypes } from '@/config/accepted_file_types';
import dig from '@/functions/dig';
import useAjax from '@/functions/use_ajax';
import useUploader from '@/vendor/use_uploader';
import { useRouter } from 'vue-router';

// Components
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';
import NoticeMessageContainer from '@/components/generic/notice_message_container.vue';
import DivisionSelect from '@/components/operations/division_select.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';

const createNewEquipment = () => ({
  name: '',
  division: null,
  mine_id: '',
  equipment_type_id: '',
  equipment_process_id: '',
  map_tile_overlays: [],
});

const props = defineProps({
  organisationId: { type: String, required: true },
  equipmentId: { type: String, default: null },
});

const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const currentOrganisation = inject('currentOrganisation');
const loading = ref(false);
const initialEquipment = ref(null);
const equipment = reactive(createNewEquipment());
const errors = ref(null);
const saving = ref(false);
const divisions = ref(null);
const diagramImage = ref(null);
const mines = ref([]);
const types = ref([]);
const processes = ref([]);
let diagramUpload;

const imageValid = ref(true);
const imageValidationMessage = ref(null);
const warningText = ref(null);

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${props.organisationId}/divisions`,
  mines: `/organisations/${props.organisationId}/mines`,
  equipmentList: `/organisations/${props.organisationId}/equipment/`,
  equipment: `/organisations/${props.organisationId}/equipment/${props.equipmentId}`,
  types: `/organisations/${props.organisationId}/equipment_types`,
  processes: `/organisations/${props.organisationId}/equipment_processes`,
  get post_or_patch() {
    return props.equipmentId ? this.equipment : this.equipmentList;
  },
};

const loadDivisions = async () => {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
};

const loadMines = async () => {
  let { ok, data } = await ajax(ENDPOINTS.mines);

  if (ok) {
    mines.value = data?.map((item) => ({ value: item.id, label: item.name }));
    mines.value.unshift({ value: '', label: I18n.t('equipment.no_mine'), dim: true });
  }
};

const loadTypes = async () => {
  let { ok, data } = await ajax(ENDPOINTS.types);

  if (ok) {
    types.value = data?.map((item) => ({ value: item.id, label: item.name }));
    types.value.unshift({ value: '', label: I18n.t('equipment.no_type'), dim: true });
  }
};

const loadProcesses = async () => {
  let { ok, data } = await ajax(ENDPOINTS.processes);

  if (ok) {
    processes.value = data?.map((item) => ({ value: item.id, label: item.name }));
    processes.value.unshift({ value: '', label: I18n.t('equipment.no_process'), dim: true });
  }
};

const loadEquipment = async () => {
  if (props.equipmentId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.equipment);

    if (ok) {
      initialEquipment.value = data;
      Object.assign(equipment, data);
      diagramImage.value = data?.diagram_thumbnail_url;
      loading.value = false;
    }
  } else {
    initialEquipment.value = createNewEquipment();
  }
};

onMounted(() => {
  loadDivisions();
  loadMines();
  loadTypes();
  loadProcesses();
  loadEquipment();
});

const formatDivisionAttributes = () => {
  if (equipment.division?.isNew) {
    return {
      division_id: 'new',
      new_division: equipment.division.name,
    };
  } else {
    return {
      division_id: equipment.division?.id || null,
    };
  }
};

const formatEquipmentForPost = async () => {
  let equipmentParams = {
    name: equipment.name,
    mine_id: equipment.mine_id,
    equipment_type_id: equipment.equipment_type_id,
    equipment_process_id: equipment.equipment_process_id,
    ...formatDivisionAttributes(),
  };

  if (diagramUpload) {
    let blob = await uploader.upload(diagramUpload);
    equipmentParams.diagram = {
      source: blob.signed_id,
      source_filename: blob.filename,
    };
  }

  return { equipment: equipmentParams };
};

const displayMine = computed(() => {
  return equipment.mine ? equipment.mine : I18n.t('equipment.no_mine');
});

const displayType = computed(() => {
  return equipment.type ? equipment.type : I18n.t('equipment.no_type');
});

const displayProcess = computed(() => {
  return equipment.process ? equipment.process : I18n.t('equipment.no_process');
});

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = props.equipmentId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatEquipmentForPost(),
  });

  if (ok) {
    router.replace({ name: 'equipment', params: { equipmentId: data.id } });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

const nameErrors = computed(() => dig(errors.value, 'equipment', 'name'));
const divisionNameErrors = computed(() => dig(errors.value, 'division', 'division', 'name'));

function updateBlob(file) {
  if (!file?.upload) return;
  diagramUpload = file.upload;
}

function updateReader(file) {
  if (!file?.url) return;
  diagramImage.value = file.url;
  warningText.value = I18n.t('general.warning_text');
}

function updateError(file) {
  if (!file?.filename) return;

  if (!acceptedFileTypes.includes(file.filename)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', {
      filename: file.filename,
    });
    imageValid.value = false;
  } else {
    imageValid.value = true;
  }
}
</script>
